import { createAction } from 'redux-actions';
import { getAllLayers } from 'pxc-editor-core/helpers/schema';
import { getDictFirstKey } from 'pxc-editor-core/helpers/common';
import {
  SET_VIEWBOX_X,
  SET_VIEWBOX_Y,
  SET_VIEWBOX_DX,
  SET_VIEWBOX_DY,
  SET_TEMPORARY_VIEWBOX_Y,
  SET_TEMPORARY_VIEWBOX_X,
  TOGGLE_ZOOM_STATE,
  SET_SCALE_COEF,
  ZOOM_IN_STATE,
  ZOOM_OUT_STATE,
  ZOOM_PAGE_EDIT_STATE,
  TEXT_TYPING_SECTION_CONTENT_RESOURCE,
  INTERACTIVE_SVG_ID
} from '@/constants';
import {
  getPaginationPageItemsByPage,
  mobileAndTabletCheck,
  isIosMobileCheck,
  getScaleCoefs,
  mapPercentageIntoScaleCoef,
  getElementByQuerySelector,
  isWebComponentEnvironment
} from '@/helpers';
import {
  setTextTypingResource
} from './main';

const webComponent = isWebComponentEnvironment();

////////////// VIEWBOX PART

const setViewboxX = x => {
  return createAction(SET_VIEWBOX_X, () => x)();
}
const setViewboxY = y => {
  return createAction(SET_VIEWBOX_Y, () => y)();
}
const setViewboxDX = dx => createAction(SET_VIEWBOX_DX, () => dx)();
const setViewboxDY = dy => createAction(SET_VIEWBOX_DY, () => dy)();
const setTemporaryViewboxX = value => createAction(SET_TEMPORARY_VIEWBOX_X, () => value)();
const setTemporaryViewboxY = value => createAction(SET_TEMPORARY_VIEWBOX_Y, () => value)();

export const resetViewbox = () => dispatch => {
  dispatch(setViewboxX(0));
  dispatch(setViewboxY(0));
  dispatch(setViewboxDX(0));
  dispatch(setViewboxDY(0));
};

export const centerViewbox = () => (dispatch, getState) => {
  const { core: { pages, pagesOrder, pagesSelection, paginationPageNum, scaleCoef } } = getState();
  const visiblePagesUids = getPaginationPageItemsByPage(pagesOrder, paginationPageNum);
  let targetPageUid = pagesSelection[0];
  if (visiblePagesUids.indexOf(targetPageUid) < 0) {
    targetPageUid = visiblePagesUids[0];
  }
  if (targetPageUid) {
    const {
      box: { r: pageWidth, b: pageHeight },
    } = pages[targetPageUid];

    const rendererRect = getElementByQuerySelector(`#${INTERACTIVE_SVG_ID}`)?.getBoundingClientRect();

    if (rendererRect) {
      const newViewboxY = (-rendererRect.height + pageHeight * scaleCoef) / 2;
      dispatch(setViewboxY(newViewboxY));
  
      const pageOffset = (-rendererRect.width + pageWidth * visiblePagesUids.length * scaleCoef) / 2;
      dispatch(setViewboxX(pageOffset));
    }
  }
};

export const centerViewboxToChoosedElement = () => (dispatch, getState) => {
  const { core: { scaleCoef, shieldsSelection } } = getState();
  const selectedShape = Object.values(shieldsSelection || {})[0];

  if(selectedShape) {
    const {x: shapeX, y: shapeY, w: shapeWidth, h: shapeHeight} = selectedShape.primitiveProps;

    const rendererRect = getElementByQuerySelector(`#${INTERACTIVE_SVG_ID}`)?.getBoundingClientRect();
	if (rendererRect) {
    	const shapeViewboxY = (-rendererRect.height + 2 * shapeY * scaleCoef + shapeHeight * scaleCoef) / 2;
    	const shapeViewboxX = (-rendererRect.width + 2 * shapeX * scaleCoef + shapeWidth * scaleCoef) / 2;

      dispatch(setViewboxY(shapeViewboxY));
      dispatch(setViewboxX(shapeViewboxX));
    }

  } else {
    dispatch(centerViewbox());
  }
};

export const centerViewboxAfterRender = () => dispatch => {
  setTimeout(() => {
    dispatch(centerViewbox());
  }, 0);
};

export const handlePageDragEnd = (dx, dy) => (dispatch, getState) => {
  const { core: { viewboxX, viewboxY }  } = getState();
  dispatch(setViewboxX(viewboxX - dx));
  dispatch(setViewboxY(viewboxY - dy));
};

export const handleKeyboardInputFocus = () => (dispatch, getState) => {
  dispatch(setTextTypingResource(TEXT_TYPING_SECTION_CONTENT_RESOURCE));

  if (webComponent) {
    return;
  }

  // android and ios have different behavior on virtual keyboard appear.
  // so our custom logic apply only to android
  if (mobileAndTabletCheck() && !isIosMobileCheck()) {
    // handle possible virtual keyboard appear
    const {
      core: {
        sectionsSelection,
        pages,
        pagesOrder,
        paginationPageNum,
        viewboxX,
        viewboxY,
        scaleCoef,
        textTypingResource
      }
    } = getState();
    const { page, section } = getAllLayers(getDictFirstKey(sectionsSelection), pages);

    if (section && textTypingResource === TEXT_TYPING_SECTION_CONTENT_RESOURCE) {
      const rendererRect = getElementByQuerySelector(`#${INTERACTIVE_SVG_ID}`)?.getBoundingClientRect();
      if (rendererRect) {
      	dispatch(setTemporaryViewboxX(viewboxX));
      	dispatch(setTemporaryViewboxY(viewboxY));
      }

      const visiblePagesUids = getPaginationPageItemsByPage(pagesOrder, paginationPageNum);

      let leftPagesWidth = 0;
      for (const uidIndx in visiblePagesUids) {
        if (visiblePagesUids[uidIndx] === page.uid) {
          break;
        }
        leftPagesWidth += pages[visiblePagesUids[uidIndx]].box.r;
      }

      // TODO: refactor to one action
      dispatch(setViewboxX(
        section.x * scaleCoef + // section left offset
        leftPagesWidth * scaleCoef - // add left pages
        (rendererRect.width - section.width * scaleCoef) / 2 // need display left offset
      ));
      dispatch(setViewboxY(
        section.y * scaleCoef - // section top offset
        rendererRect.height * 0.05 // need display top offset
      ));
    }
  }
};

export const handleKeyboardInputUnfocus = () => (dispatch, getState) => {
  const { core: { temporaryViewboxX, temporaryViewboxY, textTypingResource } } = getState();

  if (textTypingResource) {
    dispatch(setTextTypingResource(null));
  }
  if (textTypingResource === TEXT_TYPING_SECTION_CONTENT_RESOURCE &&
    temporaryViewboxX !== null && temporaryViewboxY !== null) {
    // TODO: refactor to one action
    dispatch(setViewboxX(temporaryViewboxX));
    dispatch(setViewboxY(temporaryViewboxY));
    dispatch(setTemporaryViewboxX(null));
    dispatch(setTemporaryViewboxY(null));
  }
};

////////////// ZOOM / SCALE PART
export const setScaleCoef = scaleCoef => createAction(SET_SCALE_COEF, () => scaleCoef)();

export const manuallyScaleSchema = (newScaleCoef, scalingCenter) => (dispatch, getState) => {
  const { core: { viewboxX, viewboxY, scaleCoef } } = getState();
  const scaleCoefs = getScaleCoefs();

  const zoomIn = mapPercentageIntoScaleCoef(600)

  let _newScaleCoef = newScaleCoef;
  if (newScaleCoef >= zoomIn) {
    _newScaleCoef = (scaleCoef !== zoomIn) ? zoomIn : null;
    if (_newScaleCoef) {
      dispatch(createAction(TOGGLE_ZOOM_STATE, () => ZOOM_IN_STATE)());
    }
  } else  {
    dispatch(createAction(TOGGLE_ZOOM_STATE, () => ZOOM_OUT_STATE)());
    const minBorder = scaleCoefs.zoomOut * 0.5;
    if (newScaleCoef < minBorder) {
      _newScaleCoef = scaleCoef !== minBorder ? minBorder : null;
    }
  }
  if (_newScaleCoef) {
    dispatch(setScaleCoef(_newScaleCoef));
    if (scalingCenter) {
      const moveX = scalingCenter.x * _newScaleCoef - scalingCenter.x * scaleCoef;
      const moveY = scalingCenter.y * _newScaleCoef - scalingCenter.y * scaleCoef;

      dispatch(setViewboxX(viewboxX + moveX));
      dispatch(setViewboxY(viewboxY + moveY));
    }
  }
};

export const scaleSchema = zoomState => (dispatch) => {
  const scaleCoefs = getScaleCoefs();
  let newScaleCoef = 1;
  switch (zoomState) {
    case ZOOM_IN_STATE:
      newScaleCoef = mapPercentageIntoScaleCoef(100);
      break;
    case ZOOM_OUT_STATE:
      newScaleCoef = scaleCoefs.zoomOut;
      break;
    case ZOOM_PAGE_EDIT_STATE:
      newScaleCoef = scaleCoefs.pageMode;
      break;
    default:
      break;
  }
  dispatch(setScaleCoef(newScaleCoef));
};

export const handleZoomStateChange = (newZoomState, withContainerResize = false) => (dispatch) => {
  if (newZoomState === ZOOM_OUT_STATE || newZoomState === ZOOM_IN_STATE) {
    dispatch(createAction(TOGGLE_ZOOM_STATE, () => newZoomState)());
  }

  dispatch(scaleSchema(newZoomState));
  if (withContainerResize) {
    dispatch(centerViewboxAfterRender());
  } else {
    dispatch(centerViewbox());
  }
};

export const handleZoomClick = () => (dispatch, getState) => {
  const { core: { zoomState } } = getState();
  dispatch(handleZoomStateChange(zoomState === ZOOM_IN_STATE ? ZOOM_OUT_STATE : ZOOM_IN_STATE));
};

export const handleZoomSliderChange = (value) => (dispatch) => {
  dispatch(setScaleCoef(mapPercentageIntoScaleCoef(value)));
  dispatch(centerViewboxToChoosedElement());
};